import { FC, useEffect, useRef } from "react";

import { Box, Stack } from "@mui/material";
import { useFormContext, useFormState, useWatch } from "react-hook-form";

import { useRateContext } from "../../../../Context/RateContext";
import { useRateFieldName } from "../../../../hooks/useRateFieldName";
import { useIsExtrasSectionOpen } from "../../../ExtrasSection/ExtrasSection";

import { toFiniteNumber } from "@/common/helpers/toFiniteNumber";
import { isMin, isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

interface GroupBundleFieldsProps {
  isJustChecked?: boolean;
}

export const GroupBundleFields: FC<GroupBundleFieldsProps> = ({ isJustChecked }) => {
  const { t } = useTranslate();
  const { formCategory, isRegistration } = useRateContext();
  const { control, getFieldState } = useFormContext<StepTicketsFormData>();
  const isExtrasOpen = useIsExtrasSectionOpen();

  const unitsKey = isRegistration
    ? "ticketing.rateOption.bundleTickets.registration"
    : isSweepstakesCategory(formCategory)
      ? "ticketing.rateOption.bundleTickets.sweepstakes"
      : "ticketing.rateOption.bundleTickets";

  const fieldName = useRateFieldName("attendeesByTicket");
  const fieldFormState = useFormState({ control, name: fieldName });
  const { error } = getFieldState(fieldName, fieldFormState);
  const inputRef = useRef<HTMLDivElement | null>(null);

  const attendees = useWatch({ control, name: fieldName });

  const attendeesCount = toFiniteNumber(attendees);

  useEffect(() => {
    if (isJustChecked && isExtrasOpen && inputRef.current) {
      inputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJustChecked]);

  useEffect(() => {
    if (error && isExtrasOpen && inputRef.current) {
      inputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isExtrasOpen]);

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <DashboardTypography component="span" variant="caption" vibe="text-neutral-quiet" sx={{ whiteSpace: "nowrap" }}>
        {t("dashboard", "ticketing.rateOption.bundleOf")}
      </DashboardTypography>
      <Box sx={{ width: 50 }}>
        <DashboardTextField
          defaultValue={2}
          name={fieldName}
          control={control}
          size="small"
          disabled={isRegistration}
          error={Boolean(error?.message)}
          rules={[
            isRequired(t("dashboard", "ticketing.rateGroupNumberIsRequired")),
            isMin(2, t("dashboard", "ticketing.rateGroupMinimumNumber")),
          ]}
          type="number"
          inputRef={inputRef}
          onClick={(e) => e.stopPropagation()}
        />
      </Box>
      <DashboardTypography component="span" variant="caption" vibe="text-neutral-quiet" sx={{ whiteSpace: "nowrap" }}>
        {t("dashboard", unitsKey, { count: attendeesCount ?? 0 })}
      </DashboardTypography>
    </Stack>
  );
};
